import { apiFetch } from "@/lib/api";

export type ConversationBrief = {
  id: string;
  title: string;
  pinned: boolean;
  source: string | null;
  project_id?: string | null;
  match_message_id?: string | null;
  match_snippet?: string | null;
  match_role?: string | null;
  match_count?: number;
  created_at: string;
  updated_at: string;
};

export type MessageOut = {
  id: string;
  seq: number;
  role: "user" | "assistant";
  content: string;
  attachments: unknown[];
  status: "complete" | "streaming" | "failed";
  created_at: string;
};

export const conversationsApi = {
  list: () => apiFetch<{ items: ConversationBrief[] }>("/api/conversations"),

  create: (title?: string, projectId?: string) =>
    apiFetch<ConversationBrief>("/api/conversations", {
      method: "POST",
      body: JSON.stringify({ title, source: "web", project_id: projectId })
    }),

  detail: (id: string) =>
    apiFetch<ConversationBrief & { messages: MessageOut[] }>(`/api/conversations/${id}`),

  rename: (id: string, title: string) =>
    apiFetch<ConversationBrief>(`/api/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title })
    }),

  setPinned: (id: string, pinned: boolean) =>
    apiFetch<ConversationBrief>(`/api/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ pinned })
    }),

  remove: (id: string) => apiFetch<void>(`/api/conversations/${id}`, { method: "DELETE" }),

  clearAll: () => apiFetch<void>("/api/conversations", { method: "DELETE" }),

  search: (q: string) =>
    apiFetch<{ items: ConversationBrief[] }>(
      `/api/conversations/search?q=${encodeURIComponent(q)}`
    ),

  appendMessage: (
    convId: string,
    msg: {
      role: "user" | "assistant";
      content?: string;
      attachments?: unknown[];
      status?: string;
    }
  ) =>
    apiFetch<MessageOut>(`/api/conversations/${convId}/messages`, {
      method: "POST",
      body: JSON.stringify(msg)
    }),

  finishMessage: (convId: string, msgId: string, content: string, status = "complete") =>
    apiFetch<MessageOut>(`/api/conversations/${convId}/messages/${msgId}`, {
      method: "PATCH",
      body: JSON.stringify({ content, status })
    }),

  importLegacy: (sessions: unknown[]) =>
    apiFetch<{ imported: number }>("/api/conversations/import", {
      method: "POST",
      body: JSON.stringify({ sessions })
    })
};

export type ShareRow = {
  id: string;
  token: string;
  title: string;
  created_at: string;
  message_count: number;
  view_count: number;
  last_viewed_at: string | null;
};

export type PublicShare = {
  title: string;
  created_at: string;
  messages: { role: string; content: string; has_attachments: boolean }[];
};

export const sharesApi = {
  /** 把对话的**当前内容**快照成一条公开链接。之后继续聊不会改变已发出去的链接。 */
  create: (convId: string, messageIds?: string[]) =>
    apiFetch<{ id: string; token: string; title: string; created_at: string; message_count: number }>(
      `/api/conversations/${convId}/share`,
      {
        method: "POST",
        // 不传就是整段分享；传了只分享勾选的那几条。
        // 按 id 而不是下标——下标会随对话增长错位。
        body: JSON.stringify({ message_ids: messageIds ?? null })
      }
    ),
  list: () => apiFetch<ShareRow[]>("/api/shares"),
  revoke: (id: string) => apiFetch<void>(`/api/shares/${id}`, { method: "DELETE" }),
  /** 公开读取，不带登录态——分享页给没有账号的人看 */
  read: (token: string) => apiFetch<PublicShare>(`/api/s/${token}`, { auth: false })
};
